<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { Calendar } from 'headless-calendar'
import {
  User, CheckCircle, Clock, Calendar as CalendarIcon, Video, StickyNote, BookOpen,
  CalendarDays, CalendarRange, List, ChevronLeft, ChevronRight, Hand, Loader2, CalendarClock, CalendarSync,
} from 'lucide-vue-next'

const breakpoints = useBreakpoints(breakpointsTailwind)

const props = defineProps({
  sessions: { type: Array, default: () => [] },
  currentMonth: { type: String, default: '' },
  showHeader: { type: Boolean, default: true },
  showNavigation: { type: Boolean, default: true },
  showViewToggle: { type: Boolean, default: true },
  showEnrollments: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  defaultView: { type: String, default: 'month' },
  headerGradient: { type: String, default: 'from-teal-500 to-teal-800' },
  legendHint: { type: String, default: 'Click on a session for details' },
})

const emit = defineEmits(['sessionClick', 'monthChange', 'statsChange', 'syncCalendar'])

// ─── State ───

const daySelected = ref('')
const daySessions = ref([])

function parseMonth(str) {
  if (!str) {
    const now = new Date()
    return [now.getFullYear(), now.getMonth() + 1]
  }
  return str.split('-').map(Number)
}

const [initYear, initMonth] = parseMonth(props.currentMonth)
const displayedYear = ref(initYear)
const displayedMonth = ref(initMonth)

const currentView = ref(breakpoints.isSmaller('md') ? 'week' : props.defaultView)

// Sync when parent updates currentMonth prop
watch(() => props.currentMonth, (val) => {
  if (!val) return
  const [y, m] = val.split('-').map(Number)
  displayedYear.value = y
  displayedMonth.value = m
})

// ─── Headless Calendar Grid ───

function getMonthGridRange(year, month) {
  const firstOfMonth = new Date(Date.UTC(year, month - 1, 1))
  const lastOfMonth = new Date(Date.UTC(year, month, 0))

  const dow = firstOfMonth.getUTCDay()
  const mondayOffset = dow === 0 ? 6 : dow - 1
  const gridStart = new Date(firstOfMonth)
  gridStart.setUTCDate(gridStart.getUTCDate() - mondayOffset)

  const lastDow = lastOfMonth.getUTCDay()
  const sundayOffset = lastDow === 0 ? 0 : 7 - lastDow
  const gridEnd = new Date(lastOfMonth)
  gridEnd.setUTCDate(gridEnd.getUTCDate() + sundayOffset)

  const startStr = `${gridStart.getUTCFullYear()}-${gridStart.getUTCMonth() + 1}-${gridStart.getUTCDate()}`
  const endStr = `${gridEnd.getUTCFullYear()}-${gridEnd.getUTCMonth() + 1}-${gridEnd.getUTCDate()}`

  return Calendar.custom(startStr, endStr, { startWeekdayIndex: 1 })
}

function formatDateStr(day) {
  return `${day.year}-${String(day.month).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`
}

const calendarGrid = computed(() => {
  const cal = getMonthGridRange(displayedYear.value, displayedMonth.value)
  const sessionsMap = new Map()
  const sorted = [...props.sessions].sort((a, b) => (a.raw_time || a.time || '').localeCompare(b.raw_time || b.time || ''))
  sorted.forEach(s => {
    const list = sessionsMap.get(s.date) || []
    list.push(s)
    sessionsMap.set(s.date, list)
  })

  const today = new Date()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

  const todayDay = today.getDay()
  const mondayOff = todayDay === 0 ? -6 : 1 - todayDay
  const weekStart = new Date(today)
  weekStart.setDate(today.getDate() + mondayOff)
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 6)

  return [...cal].map(day => {
    const dateStr = formatDateStr(day)
    const jsDate = new Date(day.year, day.month - 1, day.day)
    const isInCurrentWeek = jsDate >= weekStart && jsDate <= weekEnd

    return {
      ...day,
      dateStr,
      isCurrentMonth: day.month === displayedMonth.value,
      isToday: dateStr === todayStr,
      isCurrentWeek: isInCurrentWeek,
      isWeekStart: jsDate.getTime() === weekStart.getTime(),
      isWeekEnd: jsDate.getTime() === weekEnd.getTime(),
      sessions: sessionsMap.get(dateStr) || [],
    }
  })
})

const weekdayNames = computed(() => {
  const cal = getMonthGridRange(displayedYear.value, displayedMonth.value)
  return breakpoints.isGreaterOrEqual('md')
    ? cal.getWeekdayNames('long').map(n => n.toUpperCase())
    : cal.getWeekdayNames('short').map(n => n.toUpperCase())
})

const calendarRows = computed(() => {
  const rows = []
  for (let i = 0; i < calendarGrid.value.length; i += 7) {
    rows.push(calendarGrid.value.slice(i, i + 7))
  }
  return rows
})

const weekDays = computed(() => {
  const todayRow = calendarRows.value.find(row => row.some(d => d.isToday))
  return todayRow || calendarRows.value[0] || []
})

const displayedDays = computed(() => {
  return currentView.value === 'week' ? weekDays.value : calendarGrid.value
})

// ─── Navigation ───

// Navigation limits: 1 month before today, unlimited forward
const now = new Date()
const todayYear = now.getFullYear()
const todayMonth = now.getMonth() + 1 // 1-indexed

function getMinMonth() {
  return todayMonth === 1 ? { year: todayYear - 1, month: 12 } : { year: todayYear, month: todayMonth - 1 }
}

const canNavigateBackward = computed(() => {
  const min = getMinMonth()
  if (displayedYear.value > min.year) return true
  return displayedYear.value === min.year && displayedMonth.value > min.month
})

const canNavigateForward = computed(() => true)

const monthName = computed(() => {
  return new Date(displayedYear.value, displayedMonth.value - 1)
    .toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
})

const currentWeekLabel = computed(() => {
  if (weekDays.value.length === 0) return ''
  const start = weekDays.value[0]
  const end = weekDays.value[6]
  const startDate = new Date(start.year, start.month - 1, start.day)
  const endDate = new Date(end.year, end.month - 1, end.day)
  // British order, "5 – 11 October 2026": the start repeats only what differs from the end.
  const startStr = startDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: start.month === end.month ? undefined : 'long',
    year: start.year === end.year ? undefined : 'numeric',
  })
  const endStr = endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  return `${startStr} – ${endStr}`
})

// Sessions scoped to the displayed month
const displayedMonthSessions = computed(() => {
  const prefix = `${displayedYear.value}-${String(displayedMonth.value).padStart(2, '0')}`
  return props.sessions.filter(s => s.date && s.date.startsWith(prefix))
})

// Sessions visible in the current view
const visibleSessions = computed(() => {
  if (currentView.value === 'week') {
    const weekDateStrs = new Set(weekDays.value.map(d => d.dateStr))
    return props.sessions.filter(s => s.date && weekDateStrs.has(s.date))
  }
  return displayedMonthSessions.value
})

const visibleSessionCounts = computed(() => {
  const sessions = visibleSessions.value.filter(s => !isEmptySession(s))
  const totalMinutes = sessions.reduce((sum, s) => sum + (s.duration_minutes || 0), 0)
  const totalHours = Math.ceil(totalMinutes / 60)

  // Find next upcoming session
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const upcoming = sessions
    .filter(s => s.date && new Date(s.date) >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
  const next = upcoming[0] || null
  const nextSession = next ? {
    date: new Date(next.date).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' }),
    time: next.raw_time ? new Date(`2000-01-01T${next.raw_time}`).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' }) : null,
    subject: next.subject_name || 'Session',
    type: next.type || 'one-on-one',
  } : null

  return { total: sessions.length, totalHours, nextSession }
})

const statsPeriodLabel = computed(() => {
  if (currentView.value === 'week') return 'Sessions in Week'
  return `Sessions in ${new Date(displayedYear.value, displayedMonth.value - 1).toLocaleDateString('en-GB', { month: 'long' })}`
})

watch([visibleSessionCounts, statsPeriodLabel], ([counts, periodLabel]) => {
  emit('statsChange', {
    sessionsThisMonth: counts.total,
    totalHours: counts.totalHours,
    nextSession: counts.nextSession,
    periodLabel,
  })
}, { immediate: true })

function headerTitle() {
  if (currentView.value === 'week') return `Current Week: ${currentWeekLabel.value}`
  if (currentView.value === 'timeline') return `${monthName.value} Schedule`
  return monthName.value
}

function sessionCountLabel() {
  const { total } = visibleSessionCounts.value
  if (total === 0) return 'No sessions'

  // Every session is 1:1, so the breakdown this used to carry — "4 sessions
  // (4 1:1)" — only ever restated the number in front of it.
  return `${total} session${total !== 1 ? 's' : ''}`
}

function headerSubtitle() {
  const period = currentView.value === 'week' ? 'this week' : 'this month'
  return `${sessionCountLabel()} ${period}`
}

function emitMonthChange(year, month) {
  const monthStr = `${year}-${String(month).padStart(2, '0')}`
  emit('monthChange', { year, month, monthStr })
}

function navigateForward() {
  if (!canNavigateForward.value) return
  let m = displayedMonth.value + 1
  let y = displayedYear.value
  if (m > 12) { m = 1; y++ }
  displayedMonth.value = m
  displayedYear.value = y
  emitMonthChange(y, m)
}

function navigateBackward() {
  if (!canNavigateBackward.value) return
  let m = displayedMonth.value - 1
  let y = displayedYear.value
  if (m < 1) { m = 12; y-- }
  displayedMonth.value = m
  displayedYear.value = y
  emitMonthChange(y, m)
}

function navigateToday() {
  displayedYear.value = todayYear
  displayedMonth.value = todayMonth
  emitMonthChange(todayYear, todayMonth)
}

// ─── Color Palette ───

const colorPalette = [
  { bg: 'bg-blue-100', border: 'border-l-blue-600', badge: 'bg-blue-600', text: 'text-blue-800' },
  { bg: 'bg-purple-100', border: 'border-l-purple-600', badge: 'bg-purple-600', text: 'text-purple-800' },
  { bg: 'bg-emerald-100', border: 'border-l-emerald-600', badge: 'bg-emerald-600', text: 'text-emerald-800' },
  { bg: 'bg-amber-100', border: 'border-l-amber-600', badge: 'bg-amber-600', text: 'text-amber-800' },
  { bg: 'bg-rose-100', border: 'border-l-rose-600', badge: 'bg-rose-600', text: 'text-rose-800' },
  { bg: 'bg-cyan-100', border: 'border-l-cyan-600', badge: 'bg-cyan-600', text: 'text-cyan-800' },
  { bg: 'bg-indigo-100', border: 'border-l-indigo-600', badge: 'bg-indigo-600', text: 'text-indigo-800' },
  { bg: 'bg-teal-100', border: 'border-l-teal-600', badge: 'bg-teal-600', text: 'text-teal-800' },
  { bg: 'bg-orange-100', border: 'border-l-orange-600', badge: 'bg-orange-600', text: 'text-orange-800' },
  { bg: 'bg-pink-100', border: 'border-l-pink-600', badge: 'bg-pink-600', text: 'text-pink-800' },
]

const timelineColorPalette = [
  { bg: 'bg-blue-50', border: 'border-l-blue-500', accent: 'bg-blue-500' },
  { bg: 'bg-purple-50', border: 'border-l-purple-500', accent: 'bg-purple-500' },
  { bg: 'bg-emerald-50', border: 'border-l-emerald-500', accent: 'bg-emerald-500' },
  { bg: 'bg-amber-50', border: 'border-l-amber-500', accent: 'bg-amber-500' },
  { bg: 'bg-rose-50', border: 'border-l-rose-500', accent: 'bg-rose-500' },
  { bg: 'bg-cyan-50', border: 'border-l-cyan-500', accent: 'bg-cyan-500' },
  { bg: 'bg-indigo-50', border: 'border-l-indigo-500', accent: 'bg-indigo-500' },
  { bg: 'bg-teal-50', border: 'border-l-teal-500', accent: 'bg-teal-500' },
]

const emptySessionColor = { bg: 'bg-gray-100', border: 'border-l-gray-300', badge: 'bg-gray-400', text: 'text-gray-400' }
const emptyTimelineColor = { bg: 'bg-gray-50', border: 'border-l-gray-300', accent: 'bg-gray-300' }

const subjectLevelColorMap = computed(() => {
  const map = new Map()
  let colorIndex = 0
  props.sessions.forEach(session => {
    const key = `${session.subject_name || 'General'}-${session.level_name || ''}`
    if (!map.has(key)) {
      map.set(key, colorIndex % colorPalette.length)
      colorIndex++
    }
  })
  return map
})

function isEmptySession(session) {
  return props.showEnrollments && (session.total_enrollments || 0) === 0
}

function getSessionColor(session) {
  if (isEmptySession(session)) return emptySessionColor
  const key = `${session.subject_name || 'General'}-${session.level_name || ''}`
  return colorPalette[subjectLevelColorMap.value.get(key) || 0]
}

function getTimelineSessionColor(session) {
  if (isEmptySession(session)) return emptyTimelineColor
  const key = `${session.subject_name || 'General'}-${session.level_name || ''}`
  const idx = subjectLevelColorMap.value.get(key) || 0
  return timelineColorPalette[idx % timelineColorPalette.length]
}

function isProjectedSession(session) {
  return session.is_projected === true
}

const hasProjectedSessions = computed(() => {
  return props.sessions.some(s => s.is_projected === true)
})

// ─── Legend ───

const uniqueSubjectLevels = computed(() => {
  const seen = new Map()
  props.sessions.forEach(session => {
    const key = `${session.subject_name || 'General'}-${session.level_name || ''}`
    if (!seen.has(key)) {
      seen.set(key, {
        subject: session.subject_name || 'General',
        level: session.level_name || null,
        colorIndex: subjectLevelColorMap.value.get(key) || 0,
      })
    }
  })
  return Array.from(seen.values())
})

// ─── Timeline helpers ───

const sessionsByDate = computed(() => {
  const grouped = new Map()
  const sorted = [...displayedMonthSessions.value].sort((a, b) => {
    const d = a.date.localeCompare(b.date)
    if (d !== 0) return d
    return (a.time || '').localeCompare(b.time || '')
  })
  sorted.forEach(session => {
    const list = grouped.get(session.date) || []
    list.push(session)
    grouped.set(session.date, list)
  })
  return grouped
})

const datesWithSessions = computed(() => {
  return Array.from(sessionsByDate.value.keys()).sort()
})

function formatDateShort(dateString) {
  const date = new Date(dateString)
  return {
    day: date.getDate(),
    weekday: date.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase(),
    month: date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase(),
  }
}

function isTodayDate(dateString) {
  return new Date().toDateString() === new Date(dateString).toDateString()
}

function isPastDate(dateString) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return new Date(dateString) < today
}

// ─── Shared helpers ───

function getStatusIcon(status) {
  switch (status) {
    case 'completed':
      return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' }
    case 'ongoing':
      return { icon: Video, color: 'text-emerald-600', bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'In Progress' }
    case 'upcoming':
      return { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100', text: 'text-amber-700', label: 'Upcoming' }
    default:
      return { icon: CalendarIcon, color: 'text-blue-600', bg: 'bg-blue-100', text: 'text-blue-700', label: 'Scheduled' }
  }
}

function handleSessionClick(session) {
  emit('sessionClick', session)
}

function getEnrollmentLabel(session) {
  return session.total_enrollments ? '1 student' : 'No booking'
}
</script>

<template>
  <section>
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <!-- Header -->
      <div
        v-if="showHeader"
        :class="['bg-linear-to-r px-6 py-4 flex flex-col md:flex-row items-center gap-3 justify-between', headerGradient]"
      >
        <div class="text-center md:text-left">
          <h3 class="text-white font-bold text-lg">{{ headerTitle() }}</h3>
          <p class="text-white/80 text-sm">{{ headerSubtitle() }}</p>
        </div>
        <div class="flex flex-col md:flex-row items-center gap-3">
          <!-- View Toggle -->
          <div v-if="showViewToggle" class="flex flex-row items-center bg-white/20 backdrop-blur-sm rounded-lg p-1">
            <button
              @click="currentView = 'timeline'"
              :class="[
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                currentView === 'timeline'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-white hover:bg-white/10'
              ]"
            >
              <List class="w-4 h-4" />
              Timeline
            </button>
            <button
              @click="currentView = 'week'"
              :class="[
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                currentView === 'week'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-white hover:bg-white/10'
              ]"
            >
              <CalendarDays class="w-4 h-4" />
              Week
            </button>
            <button
              @click="currentView = 'month'"
              :class="[
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                currentView === 'month'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-white hover:bg-white/10'
              ]"
            >
              <CalendarRange class="w-4 h-4" />
              Month
            </button>
          </div>

          <!-- Navigation -->
          <div v-if="showNavigation" class="flex items-center gap-1">
            <button
              @click="navigateBackward"
              :disabled="!canNavigateBackward"
              :class="['p-1.5 rounded-lg transition-colors', canNavigateBackward ? 'text-white hover:bg-white/15' : 'text-white/30 cursor-not-allowed']"
            >
              <ChevronLeft class="w-5 h-5" />
            </button>
            <button @click="navigateToday" class="px-3 py-1.5 rounded-lg text-white text-sm font-semibold border border-white/30 hover:bg-white/15 transition-colors">
              Today
            </button>
            <button
              @click="navigateForward"
              :disabled="!canNavigateForward"
              :class="['p-1.5 rounded-lg transition-colors', canNavigateForward ? 'text-white hover:bg-white/15' : 'text-white/30 cursor-not-allowed']"
            >
              <ChevronRight class="w-5 h-5" />
            </button>
            <Loader2 v-if="isLoading" class="w-5 h-5 text-white animate-spin ml-1" />
            <button
              @click="emit('syncCalendar')"
              class="p-1.5 rounded-lg text-white hover:bg-white/15 transition-colors ml-1"
              title="Sync to calendar app"
            >
              <CalendarSync class="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <!-- ═══ Timeline View ═══ -->
      <div v-if="currentView === 'timeline'" class="p-6">
        <div v-if="isLoading" class="text-center py-12">
          <Loader2 class="w-12 h-12 text-gray-300 mx-auto mb-3 animate-spin" />
          <p class="text-gray-500 font-medium">Loading sessions...</p>
        </div>
        <div v-else-if="datesWithSessions.length === 0" class="text-center py-12">
          <CalendarIcon class="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p class="text-gray-500 font-medium">No sessions scheduled this month</p>
          <p class="text-gray-400 text-sm mt-1">Sessions will appear here when scheduled</p>
        </div>

        <div v-else class="relative">
          <div class="absolute left-15 top-0 bottom-0 w-0.5 bg-gray-200"></div>

          <div
            v-for="(date, dateIndex) in datesWithSessions"
            :key="date"
            class="relative"
            :class="{ 'mb-8': dateIndex < datesWithSessions.length - 1 }"
          >
            <div class="flex items-start gap-6">
              <div class="relative z-10 w-18 shrink-0 text-center">
                <div
                  :class="[
                    'w-16 h-16 rounded-xl flex flex-col items-center justify-center shadow-sm border',
                    isTodayDate(date)
                      ? 'bg-linear-to-br from-teal-500 to-teal-800 text-white border-transparent'
                      : isPastDate(date)
                        ? 'bg-gray-100 text-gray-500 border-gray-200'
                        : 'bg-white text-gray-900 border-gray-200'
                  ]"
                >
                  <span class="text-[10px] font-bold tracking-wider">{{ formatDateShort(date).weekday }}</span>
                  <span class="text-2xl font-bold leading-none">{{ formatDateShort(date).day }}</span>
                  <span class="text-[10px] font-medium">{{ formatDateShort(date).month }}</span>
                </div>
                <span
                  v-if="isTodayDate(date)"
                  class="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-purple-600 whitespace-nowrap"
                >
                  TODAY
                </span>
              </div>

              <div class="flex-1 space-y-3 pt-1">
                <div
                  v-for="session in sessionsByDate.get(date)"
                  :key="session.id"
                  :class="[
                    'group relative p-4 rounded-xl border-l-4 cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5',
                    getTimelineSessionColor(session).bg,
                    getTimelineSessionColor(session).border,
                    'border border-gray-100',
                    isEmptySession(session) ? 'opacity-60' : '',
                    isProjectedSession(session) ? 'border-dashed opacity-75' : ''
                  ]"
                  @click="handleSessionClick(session)"
                >
                  <div class="flex items-start justify-between gap-4">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <h4 :class="['font-semibold truncate', isEmptySession(session) ? 'text-gray-400' : 'text-gray-900']">{{ session.title }}</h4>
                        <span
                          :class="[
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold',
                            isEmptySession(session)
                              ? 'bg-gray-100 text-gray-400'
                              : 'bg-emerald-100 text-emerald-700'
                          ]"
                        >
                          <User class="w-3 h-3" />
                          1:1
                        </span>
                        <CalendarClock v-if="isProjectedSession(session)" class="w-4 h-4 text-gray-400" title="Projected session" />
                        <StickyNote v-if="session.note" class="w-4 h-4 text-amber-500" title="Has note" />
                      </div>

                      <p v-if="session.subject_name" :class="['text-sm flex items-center gap-1.5 mb-2', isEmptySession(session) ? 'text-gray-400' : 'text-gray-600']">
                        <BookOpen class="w-3.5 h-3.5" />
                        {{ session.level_name ? `${session.level_name} ` : '' }}{{ session.subject_name }}
                      </p>

                      <div class="flex items-center gap-4 text-sm">
                        <span :class="['flex items-center gap-1.5 font-medium', isEmptySession(session) ? 'text-gray-400' : 'text-gray-700']">
                          <Clock class="w-4 h-4 text-gray-400" />
                          {{ session.time }}
                          <span v-if="session.duration_minutes" :class="['font-normal', isEmptySession(session) ? 'text-gray-400' : 'text-gray-500']">
                            ({{ session.duration_minutes }} min)
                          </span>
                        </span>
                        <span v-if="showEnrollments" :class="['flex items-center gap-1.5', isEmptySession(session) ? 'text-gray-400' : 'text-gray-600']">
                          <User class="w-4 h-4 text-gray-400" />
                          {{ getEnrollmentLabel(session) }}
                        </span>
                      </div>

                      <p v-if="session.topic" :class="['mt-2 text-sm bg-white/60 rounded-lg px-3 py-1.5 inline-block', isEmptySession(session) ? 'text-gray-400' : 'text-gray-600']">
                        <span class="font-medium">Topic:</span> {{ session.topic }}
                      </p>
                    </div>

                    <div class="flex items-center gap-3 shrink-0">
                      <span
                        :class="[
                          'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold',
                          isEmptySession(session) ? 'bg-gray-100 text-gray-400' : [getStatusIcon(session.status).bg, getStatusIcon(session.status).text]
                        ]"
                      >
                        <component :is="getStatusIcon(session.status).icon" class="w-3 h-3" />
                        {{ getStatusIcon(session.status).label }}
                      </span>
                      <ChevronRight class="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ Month / Week Grid View ═══ -->
      <template v-else>
        <div class="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          <div
            v-for="(name, i) in weekdayNames"
            :key="name"
            :class="['p-3 text-center', i < 6 ? 'border-r border-gray-200' : '']"
          >
            <p class="text-xs font-bold text-gray-700">{{ name }}</p>
          </div>
        </div>

        <div class="grid grid-cols-7">
          <div
            v-for="(day, index) in displayedDays"
            :key="day.dateStr"
            :class="[
              'flex flex-col items-center md:block p-2 border-b border-gray-200',
              currentView === 'month' ? 'md:min-h-35' : 'md:min-h-50',
              index % 7 !== 6 ? 'border-r' : '',
              !day.isCurrentMonth ? 'bg-gray-50' : '',
              day.dateStr === daySelected ? 'day-selected' : '',
              day.isCurrentWeek && day.isCurrentMonth && currentView === 'month' ? 'bg-blue-50' : '',
              day.isWeekStart && currentView === 'month' ? 'border-l-4 border-l-blue-600' : '',
              day.isWeekEnd && currentView === 'month' ? 'border-r-4 border-r-blue-600' : ''
            ]"
            @click="daySelected = day.dateStr; daySessions = day.sessions"
          >
            <div class="flex items-center justify-between mb-2">
              <p
                :class="[
                  'font-semibold w-7 h-7 flex items-center justify-center',
                  !day.isCurrentMonth ? 'text-xs text-gray-400' : 'text-sm text-gray-900',
                  day.isCurrentWeek && day.isCurrentMonth ? 'font-bold' : '',
                  day.isToday ? 'bg-blue-600 text-white rounded-full' : ''
                ]"
              >
                {{ day.day }}
              </p>
              <span
                v-if="day.isWeekStart && day.isCurrentMonth && currentView === 'month'"
                class="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full font-semibold"
              >
                This Week
              </span>
            </div>

            <div class="space-y-1.5 hidden md:block">
              <div
                v-for="session in day.sessions"
                :key="session.id"
                :class="[
                  'rounded px-2 py-1.5 border-l-2 cursor-pointer hover:shadow-md transition-shadow',
                  getSessionColor(session).bg,
                  getSessionColor(session).border,
                  isEmptySession(session) ? 'opacity-60' : '',
                  isProjectedSession(session) ? 'border-dashed opacity-75' : ''
                ]"
                @click.stop="handleSessionClick(session)"
              >
                <div class="flex items-center justify-between gap-1">
                  <span :class="['text-xs font-bold truncate flex-1', isEmptySession(session) ? 'text-gray-400' : 'text-gray-900']">{{ session.title }}</span>
                  <div class="flex items-center gap-1 shrink-0">
                    <CalendarClock v-if="isProjectedSession(session)" class="w-3 h-3 text-gray-400" title="Projected session" />
                    <StickyNote v-if="session.note" class="w-3 h-3 text-amber-600" title="Has note" />
                    <span
                      :class="[
                        'text-xs text-white px-1.5 py-0.5 rounded font-semibold',
                        isEmptySession(session) ? 'bg-gray-400' : 'bg-emerald-600'
                      ]"
                    >
                      1:1
                    </span>
                  </div>
                </div>
                <p :class="['text-xs', isEmptySession(session) ? 'text-gray-400' : 'text-gray-700']">{{ session.time }}</p>
                <p v-if="showEnrollments" :class="['text-xs flex items-center mt-0.5', isEmptySession(session) ? 'text-gray-400' : 'text-gray-600']">
                  <User class="w-3 h-3 mr-1" />
                  {{ getEnrollmentLabel(session) }}
                </p>
                <p :class="['text-xs font-semibold mt-1 flex items-center', isEmptySession(session) ? 'text-gray-400' : getStatusIcon(session.status).color]">
                  <component :is="getStatusIcon(session.status).icon" class="w-3 h-3 mr-1" />
                  {{ getStatusIcon(session.status).label }}
                </p>
              </div>
            </div>

            <div v-if="day.sessions.length" :class="['md:hidden w-4 h-4 rounded-full', getSessionColor(day.sessions[0]).badge, isEmptySession(day.sessions[0]) ? 'opacity-50' : '']"></div>
          </div>
        </div>

        <!-- Mobile day sessions -->
        <template v-if="daySessions.length">
          <div class="p-4 md:hidden">
            <div
              v-for="session in daySessions"
              :key="session.id"
              :class="[
                'rounded px-2 py-1.5 border-l-2 cursor-pointer hover:shadow-md transition-shadow',
                getSessionColor(session).bg,
                getSessionColor(session).border,
                isEmptySession(session) ? 'opacity-60' : '',
                isProjectedSession(session) ? 'border-dashed opacity-75' : ''
              ]"
              @click="handleSessionClick(session)"
            >
              <div class="flex items-center justify-between gap-1">
                <span :class="['text-xs font-bold truncate flex-1', isEmptySession(session) ? 'text-gray-400' : 'text-gray-900']">{{ session.title }}</span>
                <div class="flex items-center gap-1 shrink-0">
                  <CalendarClock v-if="isProjectedSession(session)" class="w-3 h-3 text-gray-400" title="Projected session" />
                  <StickyNote v-if="session.note" class="w-3 h-3 text-amber-600" title="Has note" />
                  <span
                    :class="[
                      'text-xs text-white px-1.5 py-0.5 rounded font-semibold',
                      isEmptySession(session) ? 'bg-gray-400' : 'bg-emerald-600'
                    ]"
                  >
                    1:1
                  </span>
                </div>
              </div>
              <p :class="['text-xs', isEmptySession(session) ? 'text-gray-400' : 'text-gray-700']">{{ session.time }}</p>
              <p v-if="showEnrollments" :class="['text-xs flex items-center mt-0.5', isEmptySession(session) ? 'text-gray-400' : 'text-gray-600']">
                <User class="w-3 h-3 mr-1" />
                {{ getEnrollmentLabel(session) }}
              </p>
              <p :class="['text-xs font-semibold mt-1 flex items-center', isEmptySession(session) ? 'text-gray-400' : getStatusIcon(session.status).color]">
                <component :is="getStatusIcon(session.status).icon" class="w-3 h-3 mr-1" />
                {{ getStatusIcon(session.status).label }}
              </p>
            </div>
          </div>
        </template>
      </template>

      <!-- Legend -->
      <div class="p-6 bg-gray-50 border-t border-gray-200">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex flex-wrap items-center gap-4">
            <span class="hidden md:inline text-sm font-medium text-gray-700">Subjects:</span>
            <div
              v-for="item in uniqueSubjectLevels"
              :key="`${item.subject}-${item.level}`"
              class="flex items-center gap-2"
            >
              <div
                :class="[
                  'w-4 h-4 rounded border-l-2',
                  colorPalette[item.colorIndex].bg,
                  colorPalette[item.colorIndex].border
                ]"
              ></div>
              <span class="text-sm text-gray-600">
                {{ item.level ? `${item.level} ${item.subject}` : item.subject }}
              </span>
            </div>
            <div v-if="hasProjectedSessions" class="flex items-center gap-2">
              <CalendarClock class="w-4 h-4 text-gray-400" />
              <span class="text-sm text-gray-500 italic">Projected</span>
            </div>
          </div>

          <button class="hidden md:flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm hover:bg-gray-50 transition-colors">
            <Hand class="w-4 h-4 text-gray-600" />
            <p class="text-sm text-gray-700 font-medium">{{ legendHint }}</p>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<style>
  @reference "tailwindcss";
  .day-selected {
    @apply relative;
    @variant after {
      @apply inset-0 absolute rounded-2xl border-2 border-blue-600;
    }
  }
</style>
